import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from '@meplis/environments';
import { ReloadGuardHelper } from '../../helpers/reload-guard.helper';

@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (typeof sessionStorage === 'undefined' || !this._isChunkError(error)) {
      console.error(error);
      return;
    }

    if (ReloadGuardHelper.isReloadHeld) {
      // Reloading now would drop the user out of the call / live stream.
      console.error('[ChunkErrorHandler] Chunk error during a call or live stream, not reloading:', error);
      return;
    }

    if (!ReloadGuardHelper.canReload()) {
      // Two chunk errors within the cooldown window → something is wrong beyond
      // a simple version mismatch. Log and let the app render what it can.
      console.error('[ChunkErrorHandler] Repeated chunk error after reload:', error);
      return;
    }

    if (!environment.production) {
      console.error('[ChunkErrorHandler] Chunk load error — reloading:', error);
    }

    ReloadGuardHelper.reloadToFreshUrl();
  }

  private _isChunkError(error: unknown): boolean {
    const err = error as any;

    if (err?.name === 'ChunkLoadError') return true;

    const message = (err?.message ?? err?.rejection?.message ?? '').toLowerCase();
    if (!message) return false;

    // Only real lazy-load failures. Plain "failed to fetch" / "load failed" are
    // what any rejected fetch says (a blocked analytics call, a network blip
    // while the laptop sleeps), and treating those as a stale build reloaded
    // the page out from under users mid-call.
    if (
      message.includes('loading chunk') ||
      message.includes('loading css chunk') ||
      message.includes('dynamically imported module') ||
      message.includes('importing a module script failed')
    ) return true;

    const url: string = err?.filename ?? err?.url ?? err?.request ?? '';
    if (/[./]chunk[.-][a-z0-9]+\.js/i.test(url)) return true;

    return false;
  }
}
